import React, { useEffect, useState } from "react";
import { sendDeleteRequest, sendGetRequest, sendPostRequest } from "../utils";
import { DndProvider as MultiBackendProvider } from "react-dnd-multi-backend";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Alert from "../components/Alert";
import AssignTrophySuccess from "../components/experience/AssignTrophySuccess";
import { getOrCreateReviewUUID } from "../components/experience/helper";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ReportForm from "../components/experience/ReportForm";
import GoogleReview from "../components/experience/GoogleReview";
import Overlay from "../components/experience/Overlay";
import moment from "moment-timezone";
import Trophy from "../components/experience/Trophy";
import TraitSlot from "../components/experience/TraitSlot";
import CustomDragLayer from "../components/experience/DragLayer";
import {
  HTML5toTouch,
  MAP_TRAIT_LABEL,
  TRAITS,
} from "../components/experience/constants";

moment.tz.setDefault("America/Toronto");

const trophies = ["trophy-1", "trophy-2", "trophy-3"];

const Experience = () => {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [trophyPool, setTrophyPool] = useState(trophies);
  const [assignments, setAssignments] = useState({});
  const [successModal, setSuccessModal] = useState(null);
  const [peerReviews, setPeerReviews] = useState([]);
  const [uuid, setUuid] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState({
    isReported: false,
    message: "",
  });
  const [customerData, setCustomerData] = useState({
    show: false,
    name: "",
    phone: "",
  });
  const [reportSent, setReportSent] = useState(null);
  const [showGoogleReview, setShowGoogleReview] = useState({
    show: false,
    expiresAt: null,
  });
  const [showOverlay, setShowOverlay] = useState(true);
  const [doneReview, setDoneReview] = useState(false);

  // when user lands we will create a uuid and store it in local storage with expiration date of 1 day
  // if user has a uuid in local storage, we will use it to fetch the data
  useEffect(() => {
    const removeAfter = localStorage.getItem("removeAfter");
    const lastReview = localStorage.getItem("lastReview");
    if (removeAfter) {
      if (moment(removeAfter).isBefore(moment())) {
        localStorage.removeItem("removeAfter");
        localStorage.removeItem("usr-brt-uuid");
        setDoneReview(false);
        return;
      } else {
        setDoneReview(true);
      }
    }

    // check if lastReview, user only can review once a day
    if (lastReview) {
      if (
        moment(lastReview).isAfter(moment().subtract(1, "day").endOf("day"))
      ) {
        setDoneReview(true);
      } else {
        localStorage.removeItem("lastReview");
        localStorage.removeItem("usr-brt-uuid");
        setDoneReview(false);
      }
    }

    const { uuid } = getOrCreateReviewUUID();
    setUuid(uuid);
  }, []);

  const getLatestReviews = async () => {
    const { uuid } = getOrCreateReviewUUID();
    const resp = await sendGetRequest(`employee-reviews-public?uuid=${uuid}`);
    const reviews = await resp.json();
    return reviews;
  };

  // Load initial data
  useEffect(() => {
    sendGetRequest(`employees-public`)
      .then((res) => res.text())
      .then(async (data) => {
        const parsedData = JSON.parse(data);
        setData(parsedData);

        // Initialize assignments
        let init = {};
        parsedData.forEach((emp) => {
          init[emp.id] = {
            cleanliness: null,
            reliability: null,
            fun: null,
          };
        });

        try {
          // Update assignments with existing data
          let index = 1;
          const reviews = await getLatestReviews();
          setPeerReviews(reviews);

          reviews.forEach((review) => {
            if (init[review.employee_id]) {
              init[review.employee_id] = {
                cleanliness: review.cleanliness ? `trophy-${index++}` : null,
                reliability: review.reliability ? `trophy-${index++}` : null,
                fun: review.fun ? `trophy-${index++}` : null,
              };
            }
          });

          setAssignments(init);

          // Update trophy pool
          const assignedTrophies = new Set();
          Object.values(init).forEach((empTraits) => {
            Object.values(empTraits).forEach((trophyId) => {
              if (trophyId) assignedTrophies.add(trophyId);
            });
          });

          setTrophyPool(trophies.filter((t) => !assignedTrophies.has(t)));
        } catch (error) {
          setError("Failed to fetch peer reviews");
        }
        setIsLoading(false);
      })
      .catch((err) => setError(JSON.stringify(err)));
  }, []);

  const handleDragEnd = async (item, monitor) => {
    setError(null);
    setSuccessModal(null);

    if (!monitor.didDrop()) {
      return;
    }

    const dropResult = monitor.getDropResult();
    if (!dropResult) {
      return;
    }

    const { slotId } = dropResult;
    const [, empId, trait] = slotId.split("|");
    const trophyId = item.id;

    // Check if the slot already has a trophy
    if (assignments[empId] && assignments[empId][trait]) {
      return;
    }

    const employee = data.find((emp) => emp.id === parseInt(empId));
    const trophyValue = parseInt(trophyId.replace("trophy-", ""));

    // Optimistically update UI
    setAssignments((prev) => {
      const updated = { ...prev };
      updated[empId][trait] = trophyId;
      return updated;
    });

    setTrophyPool((prev) => prev.filter((t) => t !== trophyId));

    try {
      // Send to server
      const resp = await sendPostRequest("employee-reviews-public", {
        uuid,
        employee_id: parseInt(empId),
        trait,
      });

      if (resp.ok) {
        setSuccessModal({
          employee,
          trait,
          points: trophyValue,
        });
      } else {
        // Revert changes if server request fails
        setAssignments((prev) => {
          const updated = { ...prev };
          updated[empId][trait] = null;
          return updated;
        });
        setTrophyPool((prev) => [...prev, trophyId]);
        setError("Failed to assign trophy");
      }

      const reviews = await getLatestReviews();
      setPeerReviews(reviews);
    } catch (error) {
      // Revert changes if server request fails
      setAssignments((prev) => {
        const updated = { ...prev };
        updated[empId][trait] = null;
        return updated;
      });
      setTrophyPool((prev) => [...prev, trophyId]);
      setError("Failed to assign trophy");
    }
  };

  const handleDeleteTrophy = async (empId, trait, trophy) => {
    // Update UI
    setAssignments((prev) => {
      const updated = { ...prev };
      updated[empId][trait] = null;
      return updated;
    });
    setTrophyPool((prev) => [...prev, trophy]);

    // Get review ID and delete from server
    const review = peerReviews.find(
      (review) => review.employee_id === parseInt(empId)
    );

    if (review) {
      try {
        await sendDeleteRequest(`employee-reviews-public/${review.id}`, {
          uuid,
          employee_id: parseInt(empId),
          trait,
        });
      } catch (error) {
        setError("Failed to remove trophy");
        // Revert changes
        setAssignments((prev) => {
          const updated = { ...prev };
          updated[empId][trait] = trophy;
          return updated;
        });
        setTrophyPool((prev) => prev.filter((t) => t !== trophy));
      }

      const reviews = await getLatestReviews();
      setPeerReviews(reviews);
    }
  };

  const showAnotherReportModal = () => {
    setCustomerData((prev) => ({
      ...prev,
      show: true,
    }));
  };

  const handleInputChange = (key, value) => {
    setCustomerData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCloseReportModal = () => {
    setCustomerData((prev) => ({
      ...prev,
      show: false,
    }));
  };

  const handleSubmitReport = () => {
    setCustomerData({
      show: false,
      name: "",
      phone: "",
    });

    sendPostRequest("report-problem", {
      uuid,
      name: customerData.name,
      phone: customerData.phone,
      message: reportData.message,
    });

    setReportSent(true);
  };

  const handleReportCheckboxChange = () => {
    setReportData({
      ...reportData,
      isReported: !reportData.isReported,
    });
  };

  // if trophyPool is empty, show the google review modal
  useEffect(() => {
    if (trophyPool.length === 0) {
      localStorage.setItem("lastReview", moment().toISOString());
    }

    if (trophyPool.length === 0) {
      // set 1 sec delay before showing the modal
      setTimeout(() => {
        setShowGoogleReview({
          show: true,
          expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24),
        });
      }, 1500);
    } else {
      setShowGoogleReview((prev) => ({
        ...prev,
        show: false,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trophyPool]);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      // Cleanup any pending drag operations
      setTrophyPool(trophies);
      setAssignments({});
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowOverlay(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  if (doneReview) {
    return (
      <div className="flex justify-center items-center flex-1">
        <Typography variant="h2" className="text-center text-green-600">
          Merci de votre avis!
        </Typography>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress />
      </div>
    );
  }

  return (
    <MultiBackendProvider
      options={HTML5toTouch}
      backends={HTML5toTouch.backends}
    >
      <CustomDragLayer />
      <div className="bg-slate-200 relative pb-8 flex-1">
        {showOverlay && <Overlay />}
        <Box sx={{ paddingTop: 2, flexGrow: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert error={error} containerClassName="mt-0 mb-2" />
              <Typography
                variant="h2"
                className="customer-list-header !font-semibold !text-xl text-center text-green-600 !leading-tight"
              >
                Évaluez votre expérience avec notre personnel
              </Typography>
              <div className="mt-2">
                <div
                  id="trophy-pool"
                  className="flex gap-4 rounded-lg w-[250px] h-[56px] mx-auto justify-center"
                >
                  {trophyPool.map((id) => (
                    <Trophy
                      key={id}
                      id={id}
                      isAssigned={false}
                      onDragEnd={handleDragEnd}
                    />
                  ))}
                  {Array.from({
                    length: trophies.length - trophyPool.length,
                  }).map((_, index) => (
                    <div
                      key={index}
                      className={`bg-yellow-50 text-white p-2 rounded shadow w-[56px] overflow-hidden h-[56px] flex items-center justify-center border border-yellow-500 opacity-20 cursor-not-allowed`}
                    >
                      <span className="text-yellow-500 text-3xl self-center mx-auto">
                        🏆
                      </span>
                    </div>
                  ))}
                </div>
                <span className="text-base text-center flex justify-center items-center mt-2 text-slate-600">
                  Glissez les trophés
                </span>
              </div>
              <div className="w-full max-w-md mx-auto py-0 mt-4 px-6 sm:px-0">
                <TableContainer className="max-h-[460px] rounded-xl shadow-lg [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 border-slate-300 border-t-[6px] border-b-[6px]">
                  <Table
                    stickyHeader
                    sx={{ minWidth: 350 }}
                    aria-label="employees"
                  >
                    <TableHead className="">
                      <TableRow className="">
                        <TableCell className="!text-center !border-gray-200/70">
                          &nbsp;
                        </TableCell>
                        {TRAITS.map((trait) => (
                          <TableCell
                            key={trait}
                            className="!text-center !p-2 !border-gray-200/70"
                          >
                            <div
                              className={`p-1.5 rounded-lg items-center justify-center flex ${
                                trait === "cleanliness" && "bg-green-500/20"
                              } ${
                                trait === "reliability" && "bg-blue-500/20"
                              } ${trait === "fun" && "bg-orange-500/20"}`}
                            >
                              {trait === "cleanliness" && (
                                <div className="flex flex-col items-center justify-center gap-1">
                                  <img
                                    src="/mop.png"
                                    alt="mop"
                                    className="w-5 h-5"
                                  />
                                  <span className="text-xs">
                                    {MAP_TRAIT_LABEL[trait]}
                                  </span>
                                </div>
                              )}
                              {trait === "reliability" && (
                                <div className="flex flex-col items-center justify-center gap-1">
                                  <img
                                    src="/flash.png"
                                    alt="reliability"
                                    className="w-5 h-5"
                                  />
                                  <span className="text-xs">
                                    {MAP_TRAIT_LABEL[trait]}
                                  </span>
                                </div>
                              )}
                              {trait === "fun" && (
                                <div className="flex flex-col items-center justify-center gap-1">
                                  <img
                                    src="/happiness.png"
                                    alt="party"
                                    className="w-5 h-5"
                                  />
                                  <span className="text-xs">
                                    {MAP_TRAIT_LABEL[trait]}
                                  </span>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.map((employee) => (
                        <TableRow
                          key={employee.id}
                          className=" bg-white p-6 text-center 0 my-6 mx-6"
                        >
                          <TableCell className="flex-none !text-center flex !p-2 !border-gray-100">
                            <div className="flex flex-col justify-center items-center relative">
                              <img
                                src={
                                  employee.photo
                                    ? `${process.env.REACT_APP_DOMAIN}/uploads/employees/${employee.photo}`
                                    : "/default-avatar.png"
                                }
                                alt={employee.name}
                                className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg  object-cover border border-gray-200"
                              />
                              <span className="flex w-full py-[1px] text-center justify-center rounded-t-lg shadow text-sm absolute -bottom-[9px] bg-slate-400 text-white">
                                {employee.first_name}
                              </span>
                            </div>
                          </TableCell>
                          {assignments[employee.id] &&
                            TRAITS.map((trait) => (
                              <TraitSlot
                                key={trait}
                                id={`slot|${employee.id}|${trait}`}
                                trophy={assignments[employee.id][trait]}
                                label={trait}
                                onDelete={handleDeleteTrophy}
                              />
                            ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
              {reportSent !== null ? (
                <div className="flex justify-center items-center max-w-md mx-auto mt-4">
                  <div className="text-center border-green-500 border rounded-md p-4 mx-6 sm:mx-0 bg-green-50 text-green-600">
                    Merci de nous avoir contacté. Nous vous répondrons dans les
                    plus brefs délais.
                  </div>
                </div>
              ) : (
                <div className="flex flex-col justify-start items-start max-w-md mx-auto space-y-2 mt-4 px-4 sm:px-0">
                  <div>
                    <Checkbox
                      id="report-checkbox"
                      checked={reportData.isReported}
                      onChange={handleReportCheckboxChange}
                      color="primary"
                    />
                    <FormLabel
                      htmlFor="report-checkbox"
                      variant="body1"
                      className="text-sm"
                    >
                      Signaler un problème
                    </FormLabel>
                  </div>
                  {reportData.isReported && (
                    <>
                      <div className="w-full">
                        <textarea
                          className="border border-gray-300 rounded-md p-2 resize-none w-full"
                          rows={5}
                          placeholder="Décrivez votre problème..."
                          value={reportData.message}
                          onChange={(e) =>
                            setReportData({
                              ...reportData,
                              message: e.target.value,
                            })
                          }
                          autoFocus
                        />
                      </div>
                      <div className="w-full flex justify-end">
                        <Button
                          className="w-full"
                          type="button"
                          variant="contained"
                          color="primary"
                          onClick={showAnotherReportModal}
                        >
                          Envoyer
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Grid>
          </Grid>
        </Box>
      </div>
      <AssignTrophySuccess
        openConfirmation={!!successModal}
        setOpenConfirmation={setSuccessModal}
        onAction={() => setSuccessModal(null)}
        data={successModal}
      />
      <ReportForm
        open={customerData.show}
        onClose={handleCloseReportModal}
        onAction={handleSubmitReport}
        customerData={customerData}
        handleInputChange={handleInputChange}
      />
      <GoogleReview
        open={showGoogleReview.show}
        onClose={() => {
          setShowGoogleReview((prev) => ({
            ...prev,
            show: false,
          }));

          localStorage.setItem(
            "removeAfter",
            moment().add(1, "day").startOf("day").format("YYYY-MM-DD HH:mm:ss")
          );

          setDoneReview(true);
        }}
      />
    </MultiBackendProvider>
  );
};

export default Experience;
